import React, { useCallback, useEffect } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import * as yup from "yup";
import ErrorMessageAlert, { useAlert } from "../common/Alert";
import { useAppDispatch } from "../../hooks/hooks";

import TextInputComponent from "../common/TextInputComponent";
import SelectComponent from "../common/SelectComponent";
import Autocomplete from "../common/Autocomplete";
import CheckBoxComponent from "../common/CheckBoxComponent";
import { updateElementValueCountFormReducer, useCountFormReducer } from "../../redux/slice/countFormSlice";
import { CODE_TABLE_VEHICLEMODEL, STATE_LIST } from "../../constants/common";
import { getCodeTableEntries, useComplaintReducer } from "../../redux/slice/complaintSlice";
import { addModifyEcCountsEcomplaint, updateElementValueComplaintMainReducer, useComplaintMainReducer } from "../../redux/slice/complaintMainSlice";
import YearInputComponent from "../common/YearInputComponent";

interface LeaderCountPopupProps {
  show: boolean,
  isLookupLeaderInfo?: boolean,
  onHide: () => void,
  onContinueClick: () => void,
}

const VehicleInfoPopup: React.FC<LeaderCountPopupProps> = (props) => {
  const { show, onHide, isLookupLeaderInfo, onContinueClick } = props;
  const errorMessageAlert = useAlert();
  const dispatch = useAppDispatch();
  const [errorKeys, setErrorKeys] = React.useState([""]);
  const complaintMain = useComplaintMainReducer();
  const { primaryVehicleInfoFormBean } = useCountFormReducer();
  let {
    vehicleColorList,
    vehicleMakeList,
    vehicleModelList,
    vehicleTypeList
  } = useComplaintReducer();

  const displayNonAsciiValues = (value: any) => {
    var asciiRegex = /[^\x00-\x7F]+/g;
    var cleanedText = value.match(asciiRegex);
    if (cleanedText) {
      return cleanedText;
    }
  }

  let defaultCountSchema = yup.object().shape({
    checkIfUnknown: yup.boolean().default(false),
    registration: yup.string()
      .transform((value, originalValue) => originalValue === null ? "" : value)
      .when('checkIfUnknown', {
        is: false,
        then: yup.string()
          .required('Registration is required')
          .max(9, 'Registration must not exceed 9 characters')  // Added max length validation
          .test("check-address1-valid", function (value) {
            if (value) {
              var cleanedText = displayNonAsciiValues(value);
              if (cleanedText) {
                return this.createError({
                  message: "Non printable text/characters( " + cleanedText + " ) have been pasted or added into the field Registration No. Delete all of the text and manually re-type it or copy/paste the text from notepad to attempt to remove the bad characters.",
                });
              }
              if(!value.trim()){
                return this.createError({
                  message:
                    "'Registration' is required.",
                });
              }
            }
            return true;
          }), 
        otherwise: yup.string().notRequired().max(9, 'Registration must not exceed 9 characters'), // Also adding max length for optional case
      }),
    vehicleType: yup.string()
      .nullable()
      .required("Vehicle Type is required."),
    registrationExpYear: yup.string().nullable().notRequired().max(4, 'Maximum 4 digits allowed for the "License Plate Expires (Year)"'),
    year: yup.string().nullable().notRequired().max(4, 'Maximum 4 digits allowed for the "Vehicle Model Year"'),
    vin: yup.string()
    .nullable()
    .test("check-vin-valid", function (value) {
      if (value) {
        var cleanedText = displayNonAsciiValues(value);
        if (cleanedText) {
          return this.createError({
            message: "Non printable text/characters( " + cleanedText + " ) have been pasted or added into the field VIN. Delete all of the text and manually re-type it or copy/paste the text from notepad to attempt to remove the bad characters.",
          });
        }
      }
      return true;
    }),
    make: yup.string()
    .nullable()
    .test("check-make-valid", function (value) {
      if (value) {
        var cleanedText = displayNonAsciiValues(value);
        if (cleanedText) {
          return this.createError({
            message: "Non printable text/characters( " + cleanedText + " ) have been pasted or added into the field Make. Delete all of the text and manually re-type it or copy/paste the text from notepad to attempt to remove the bad characters.",
          });
        }
      }
      return true;
    }),
    model: yup.string()
    .nullable()
    .test("check-model-valid", function (value) {
      if (value) {
        var cleanedText = displayNonAsciiValues(value);
        if (cleanedText) {
          return this.createError({
            message: "Non printable text/characters( " + cleanedText + " ) have been pasted or added into the field Model. Delete all of the text and manually re-type it or copy/paste the text from notepad to attempt to remove the bad characters.",
          });
        }
      }
      return true;
    }),
    cmv: yup.string().nullable().required("Commercial Motor Vehicle Yes/No is required."),
    hazmat: yup.string().nullable().required("Placarded HAZ Material Yes/No is required."),
    sixteenPlus: yup.string().nullable().required("16 or More Pass. Vehicle Yes/No is required."),
    dot: yup.string()
    .nullable()
    .test("check-dot-valid", function (value) {
      if (value) {
        var cleanedText = displayNonAsciiValues(value);
        if (cleanedText) {
          return this.createError({
            message: "Non printable text/characters( " + cleanedText + " ) have been pasted or added into the field DOT Number. Delete all of the text and manually re-type it or copy/paste the text from notepad to attempt to remove the bad characters.",
          });
        }
      }
      return true;
    }),
});

  const handleError = useCallback(
    (_errorKeys: string[], errorMessages: string[]) => {
      setErrorKeys(_errorKeys);
      errorMessageAlert.handleError(errorMessages);
    },
    []
  );

  useEffect(() => {
    if (primaryVehicleInfoFormBean?.year && !(/^\d+$/.test(primaryVehicleInfoFormBean.year))) {
      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['year']: ""
        }
      }));
    }
  }, [primaryVehicleInfoFormBean?.year]);

  useEffect(() => {
    if (primaryVehicleInfoFormBean?.registrationExpYear && !(/^\d+$/.test(primaryVehicleInfoFormBean.registrationExpYear))) {
      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['registrationExpYear']: ""
        }
      }));
    }
  }, [primaryVehicleInfoFormBean?.registrationExpYear]);

  useEffect(() => {
    dispatch(getCodeTableEntries({ tableName: CODE_TABLE_VEHICLEMODEL + "_" + primaryVehicleInfoFormBean?.make }));
  }, [primaryVehicleInfoFormBean?.make])

  // Handle form input change
  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    // Dispatch updated data to Redux
    dispatch(updateElementValueCountFormReducer({
      elementName: 'primaryVehicleInfoFormBean', value: {
        ...primaryVehicleInfoFormBean,
        [name]: newValue
      }
    }));

    if (name === 'checkIfUnknown' && newValue) {
      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['registration']: "",
          ['checkIfUnknown']: newValue
        }
      }));
    }
    //added by KHL 03/18/2025 PR 11673 start
    if (name === 'pedestrian' && newValue) {
      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['pedestrian']: newValue
        }
      }));
    }
    //added by KHL 03/18/2025 PR 11673 end
    if (name === 'make' && primaryVehicleInfoFormBean.model) {

      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['model']: "",
          ['make']: newValue,

        }
      }));
    }
  };

  const handleExpYearChange = (date: Date | null) => {
    if (date) {
      handleInputChange({
        target: { name: 'registrationExpYear', value: date.getFullYear().toString() }
      });

      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['registrationExpYear']: date.getFullYear().toString()
        }
      }));
    }
  };

  const handleYearChange = (date: Date | null) => {
    if (date) {
      handleInputChange({
        target: { name: 'year', value: date.getFullYear().toString() }
      });

      dispatch(updateElementValueCountFormReducer({
        elementName: 'primaryVehicleInfoFormBean', value: {
          ...primaryVehicleInfoFormBean,
          ['year']: date.getFullYear().toString()
        }
      }));
    }
  };

  const handleKeyPressOnlyNumbers = (event: any, maxLength?: number) => {
    let allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete"];
    if ((!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key))
        || (maxLength && event.target.value.length >= maxLength && !allowedKeys.includes(event.key))) {
      event.preventDefault();
    }
  }

  const addOrSaveClick = async () => {
    try {
      if (!primaryVehicleInfoFormBean.pedestrian) {
        await defaultCountSchema.validate(primaryVehicleInfoFormBean, { abortEarly: false });
      }
      let newCountList: any = [];

      for (let i = 0; i < complaintMain.countList.length; i++) {
        let element = complaintMain.countList[i];

        let count = { ...element };
        if (element.adrsos === "" || element.adrsos) {
          count.primaryVehicleInfoFormBean = primaryVehicleInfoFormBean;
        } else {
          count.primaryVehicleInfoFormBean = null;
        }
        newCountList.push(count);
      }
      dispatch(updateElementValueComplaintMainReducer({ elementName: 'countList', value: newCountList }));

      dispatch(addModifyEcCountsEcomplaint(
        {
          complaint: complaintMain.complaint,
          addModifyEcomplaint: true,
          countList: newCountList,
        }));
      cancelPopup();
      onContinueClick();
      handleError([''], []);

    } catch (err: any) {
      handleError(
        err.inner.map((e: any) => e.path),
        err.errors
      );
    }
  }

  const cancelPopup = () => {
    resetLocalState();
    dispatch(updateElementValueCountFormReducer({ elementName: 'primaryVehicleInfoFormBean', value: {} }));
    onHide();
  }

  const resetLocalState = () => {
    setErrorKeys(['']);
    handleError([''], []);

  };

  return (
    <>
      <Modal
        show={show}
        onHide={cancelPopup}
        dialogClassName="modal-55w"
        backdrop="static"
        scrollable={true}
        centered={true}
      >
        <Modal.Header closeButton>
          <Modal.Title>
          <b>Vehicle information </b>
          </Modal.Title>
        </Modal.Header>
        <br />
        <Modal.Body>
          <ErrorMessageAlert
            messages={errorMessageAlert.messages}
            clearError={errorMessageAlert.clearError}
          />
          <Row className="g-2 align-items-center mt-1">
            <Col xs={4}>
              <CheckBoxComponent
                type="checkbox"
                label={<b>Pedestrian - No Vehicle Involved</b>}
                name="pedestrian"
                checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.pedestrian}
                onChange={handleInputChange}
              />
            </Col>
          </Row>
          <Row className="g-2 align-items-center mt-1">
            <Col>

              <div id="collapse-content">
                <Row className="g-2 align-items-center mt-1">
                  <Col xs={4}>
                    <CheckBoxComponent
                      type="checkbox"
                      label={<b>Registration Not Available</b>}
                      name="checkIfUnknown"
                      checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.checkIfUnknown}
                      onChange={handleInputChange}
                      disabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                  <Col xs={4}>
                    <b>Registration No. <span className="text-danger">*</span></b>
                    <TextInputComponent
                      name="registration"
                      value={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.registration}
                      onChange={handleInputChange}
                      disabled={primaryVehicleInfoFormBean.checkIfUnknown || primaryVehicleInfoFormBean.pedestrian}
                      isError={errorKeys.includes('registration')}
                    />
                  </Col>
                  <Col xs={4}>
                    <b>State</b>
                    <SelectComponent
                      options={STATE_LIST.map(data => ({ label: data, value: data }))}
                      placeholder="-- Select --"
                      isDefaultLabel
                      onChange={(selectedOption) => handleInputChange({ target: { name: 'state', value: selectedOption?.value } })}
                      value={STATE_LIST
                        .map((data: any) => {
                          return {
                            label: data,
                            value: data
                          };
                        })
                        .find(

                          (item: any) => {
                            if (item.label = primaryVehicleInfoFormBean?.state)
                              return {
                                label: item,
                              };
                          }
                        )}
                        isDisabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                </Row>
                <Row className="g-2 align-items-center mt-1">
                  <Col xs={4}>
                    <b>License Plate Expires (Year)</b>
                    <YearInputComponent
                      name="registrationExpYear"
                      selected={
                        primaryVehicleInfoFormBean?.registrationExpYear
                          ? /^\d+$/.test(primaryVehicleInfoFormBean.registrationExpYear) ? new Date(Number(primaryVehicleInfoFormBean.registrationExpYear), 0)
                          : null : null
                      }
                      onChange={handleExpYearChange} // Make sure this function is handling year changes properly
                      onKeyPress={handleKeyPressOnlyNumbers}
                      maxLength={4}
                      isDisabled={primaryVehicleInfoFormBean.pedestrian}              
                    />
                  </Col>
                  <Col xs={4}>
                    <b>Make</b>
                    <Autocomplete
                      options={vehicleMakeList.map(data => ({ label: data.displayValue, value: data.displayValue }))}
                      placeholder="-- Select --"
                      onChange={(selectedOption) => handleInputChange({ target: { name: 'make', value: selectedOption?.value } })}
                      value={
                        primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.make
                          ? {
                            label: primaryVehicleInfoFormBean.make,
                            value: primaryVehicleInfoFormBean.make,
                          }
                          : null
                      }
                      isDisabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                  <Col xs={4}>
                    <b>Model</b>
                    <Autocomplete
                      options={vehicleModelList.map(data => ({ label: data.displayValue, value: data.displayValue }))}
                      placeholder="-- Select --"
                      onChange={(selectedOption) => handleInputChange({ target: { name: 'model', value: selectedOption?.value } })}
                      value={
                        primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.model
                          ? {
                            label: primaryVehicleInfoFormBean.model,
                            value: primaryVehicleInfoFormBean.model,
                          }
                          : null
                      }
                      isDisabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                </Row>

                <Row className="g-2 align-items-center mt-1">
                  <Col xs={4}>
                    <b>Color</b>
                    <SelectComponent
                      options={vehicleColorList.map(data => ({ label: data.displayValue, value: data.displayValue }))}
                      placeholder="-- Select --"
                      isDefaultLabel
                      onChange={(selectedOption) => handleInputChange({ target: { name: 'color', value: selectedOption?.value || selectedOption?.label, } })}
                      value={
                        primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.color
                          ? {
                            label: primaryVehicleInfoFormBean.color,
                            value: primaryVehicleInfoFormBean.color,
                          }
                          : null
                      }
                      isDisabled = {primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                  <Col xs={4}>
                    <b>Vehicle Model Year</b>
                    <YearInputComponent
                      name="year"
                      selected={
                        primaryVehicleInfoFormBean?.year
                          ? /^\d+$/.test(primaryVehicleInfoFormBean.registrationExpYear) ? new Date(Number(primaryVehicleInfoFormBean.year), 0)
                          : null : null
                      }
                      onChange={handleYearChange} // Make sure this function is handling year changes properly
                      onKeyPress={handleKeyPressOnlyNumbers}
                      maxLength={4}
                      isDisabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                  <Col xs={4}>
                    <b>Type<span className="text-danger">*</span></b>
                    <SelectComponent
                      options={vehicleTypeList.map(data => ({ label: data.displayValue, value: data.displayValue }))}
                      placeholder="-- Select --"
                      isDefaultLabel
                      onChange={(selectedOption) => handleInputChange({ target: { name: 'vehicleType', value: selectedOption?.value } })}
                      isError={errorKeys.includes('vehicleType')}
                      value={vehicleTypeList
                        .map((data: any) => {
                          return {
                            label: data,
                            value: data
                          };
                        })
                        .find(

                          (item: any) => {
                            if (item.label = primaryVehicleInfoFormBean?.vehicleType)
                              return {
                                label: item,
                              };
                          }
                        )}
                        isDisabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                </Row>
                <Row className="g-2 align-items-center mt-1">
                  <Col xs={4}>
                    <b>VIN</b>
                    <TextInputComponent
                      name="vin"
                      value={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.vin}
                      onChange={handleInputChange}
                      disabled={primaryVehicleInfoFormBean.pedestrian}
                    />
                  </Col>
                </Row>

                <Row className="g-2 align-items-center mt-1">
                  <Col xs={6}>
                    <Form.Group controlId="cmv">
                      <div className="d-flex align-items-center">
                      <Form.Label>Commercial Motor Vehicle <span className="text-danger">*</span></Form.Label>
                      <div className={
                        errorKeys.includes("cmv")
                          ? "ms-5 ps-3 pe-3 border-danger border rounded"
                          : "ps-5"
                      }>
                        <Form.Check
                          inline
                          label="Yes"
                          name="cmv"
                          type="radio"
                          onChange={handleInputChange}
                          value={"True"}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.cmv === "True"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                        <Form.Check
                          inline
                          label="No"
                          name="cmv"
                          type="radio"
                          onChange={handleInputChange}
                          value={"False"}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.cmv === "False"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                      </div>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group controlId="hazmat">
                      <div className="d-flex align-items-center">
                        <Form.Label>Placarded HAZ Material <span className="text-danger">*</span></Form.Label>
                      <div className={
                        errorKeys.includes("hazmat")
                          ? "ms-5 ps-3 pe-3 border-danger border rounded"
                          : "ps-5"
                      }>
                        <Form.Check
                          inline
                          label="Yes"
                          name="hazmat"
                          type="radio"
                          value="True"
                          onChange={handleInputChange}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.hazmat === "True"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                        <Form.Check
                          inline
                          label="No"
                          name="hazmat"
                          type="radio"
                          value="False"
                          onChange={handleInputChange}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.hazmat === "False"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2 align-items-center mt-1">
                  <Col xs={6}>
                    <Form.Group controlId="sixteenPlus">
                      <div className="d-flex align-items-center">
                        <Form.Label>16 or More Pass. Vehicle <span className="text-danger">*</span></Form.Label>
                        <div className={
                          errorKeys.includes("sixteenPlus")
                            ? "ms-5 ps-3 pe-3 border-danger border rounded"
                            : "ps-5"
                        }>
                          <Form.Check
                            inline
                          label="Yes"
                          name="sixteenPlus"
                          type="radio"
                          value="True"
                          onChange={handleInputChange}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.sixteenPlus === "True"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                        <Form.Check
                          inline
                          label="No"
                          name="sixteenPlus"
                          type="radio"
                          value="False"
                          onChange={handleInputChange}
                          checked={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.sixteenPlus === "False"}
                          disabled={primaryVehicleInfoFormBean.pedestrian}
                        />
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group controlId="dot">
                      <Form.Label>DOT Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="dot"
                        onChange={handleInputChange}
                        value={primaryVehicleInfoFormBean && primaryVehicleInfoFormBean.dot}
                        disabled={primaryVehicleInfoFormBean.pedestrian}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>

          <Col xs={6}></Col>
          <div>
            <Button onClick={addOrSaveClick}>
              SAVE VEHICLE DETAILS
            </Button>
            <>  </>
            <Button
             variant='danger'
              onClick={() => { cancelPopup() }}
            >
              CANCEL
            </Button>
          </div>

        </Modal.Footer>
      </Modal>
    </>
  )
}

export default VehicleInfoPopup;

